import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../ui/appDialogTitle';
import { SelectLocation } from '../../common';

export default function InventoryLocationActionDialog({
    open,
    onClose,
    listener,
    rows = [],
    operation = 'transfer'
}) {
    const [destinationLocation, setDestinationLocation] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setDestinationLocation(null);
            setSubmitting(false);
        }
    }, [open, operation, rows]);

    const isCopyOperation = operation === 'copy';
    const title = isCopyOperation ? 'Copy to Location' : 'Transfer to Location';

    const locationSummary = useMemo(() => {
        const names = Array.from(new Set(
            (rows || [])
                .map((row) => row?.location?.name)
                .filter(Boolean)
        ));

        if (!names.length) {
            return 'Selected items have mixed or unavailable source locations.';
        }

        if (names.length === 1) {
            return `Source location: ${names[0]}`;
        }

        return `Source locations: ${names.join(', ')}`;
    }, [rows]);

    const handleSubmit = async () => {
        if (!destinationLocation?.id) {
            toast.error('Select a destination location');
            return;
        }

        setSubmitting(true);
        try {
            if (listener) {
                await listener({
                    destinationLocation,
                    destinationLocationId: destinationLocation.id
                });
            }
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <AppDialogTitle title={title} onClose={onClose} />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Alert severity={isCopyOperation ? 'info' : 'warning'}>
                            {isCopyOperation
                                ? 'Copy keeps the source item in place and creates a new destination item with stock counts reset to zero.'
                                : 'Transfer moves the selected inventory items to the destination location.'}
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            {rows.length} item{rows.length === 1 ? '' : 's'} selected
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {locationSummary}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <SelectLocation
                            fullWidth
                            required
                            location={destinationLocation?.id || ''}
                            setLocation={setDestinationLocation}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {isCopyOperation ? 'Copy Items' : 'Transfer Items'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}